<template>
  <MainNavigationItem
    :expandable="!simpleNavigationItem"
    :label="navigationItem.name"
    :single-level-navigation="showSingleLevelNavigation"
    :to="getNavigationLink(navigationItem.path)"
    :level="level"
    exact
    @subnav-open="$emit('subnav-open', $event)"
  >
    <template #default="{ menuOpened }">
      <div class="navigation-item__subnav-list">
        <!-- Sub-Categories from goliath -->
        <SubNavigationList
          v-if="hasSubCategories"
          :category-path="navigationItem.path"
          :items="subCategories"
          :expanded="!hasSubNavListItems"
          :title="showTitle ? 'Kategorien' : undefined"
          @subnav-open="$emit('subnav-open', $event)"
        />

        <!-- custom links, like Brands -->
        <SubNavigationList
          v-if="hasSubNavListItems"
          :items="subNavListItems"
          :expanded="!hasSubCategories"
          :title="showTitle ? 'Marken' : undefined"
          @subnav-open="$emit('subnav-open', $event)"
        />
      </div>
      <div
        v-if="hasTeasers && currentNavItemTeasers?.length > 0 && menuOpened"
        class="navigation-item__teasers-wrapper"
      >
        <NavigationTeaser
          v-for="teaser in currentNavItemTeasers"
          :key="teaser.articleNumber"
          :teaser-data="teaser"
          :column-number="columnNumber"
        />
      </div>
    </template>
  </MainNavigationItem>
</template>

<script setup lang="ts">
import { computed, withDefaults } from 'vue';
import MainNavigationItem from '~/components/navigation/main-navigation-item.vue';
import SubNavigationList from '~/components/navigation/sub-navigation-list.vue';
import NavigationTeaser from '~/components/navigation/navigation-teaser.vue';
import { isAtLeastMediumDesktopScreen } from '~/composables/use-screen-size';
import type { Category, ListItem } from '~/stores/navigation';

defineEmits<{
  (e: 'subnav-open', src: number): void;
}>();

export type NavigationItemProps = {
  navigationItem?: Category | ListItem;
  level?: number;
};

const props = withDefaults(defineProps<NavigationItemProps>(), {
  navigationItem: undefined,
  level: 1,
});

const categoryItem = computed<Category | undefined>(() =>
  props.navigationItem &&
  ('listItems' in props.navigationItem || 'children' in props.navigationItem)
    ? props.navigationItem
    : undefined
);

const subNavListItems = computed(() => categoryItem.value?.listItems || []);

const hasSubNavListItems = computed(() => subNavListItems.value.length > 0);

const subCategories = computed(() => categoryItem.value?.children || []);

const hasSubCategories = computed(() => subCategories.value.length > 0);

const currentNavItemTeasers = computed(() => categoryItem.value?.teasers || []);

const hasTeasers = computed(
  () => currentNavItemTeasers.value?.length > 0 && isAtLeastMediumDesktopScreen.value
);
const simpleNavigationItem = computed(
  () => !hasSubCategories.value && !hasSubNavListItems.value && !hasTeasers.value
);
const showSingleLevelNavigation = computed(() => {
  const possibleCases = [hasSubNavListItems.value, hasSubCategories.value, hasTeasers.value];

  const trueCount = possibleCases.filter(Boolean).length;
  return trueCount === 1;
});

const columnNumber = computed(() => {
  if (!categoryItem.value) return 'one';
  if (categoryItem.value.children?.length > 20 || categoryItem.value.listItems?.length > 20)
    return 'three';
  if (categoryItem.value.children?.length > 10 || categoryItem.value.listItems?.length > 10)
    return 'two';
  return 'one';
});

const showTitle = computed(() => hasSubNavListItems.value && hasSubCategories.value);

const getNavigationLink = (path: string | undefined) => {
  if (!path) return '';
  return `/${path}`;
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

:deep(.main-navigation-item__subnav-wrapper:has(.navigation-item__smart-home-links--desktop)) {
  column-gap: 0;
}

.navigation-item {
  &__teasers-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 2 * $grid-base;
  }

  &__subnav-list {
    @include mq($mq-lg) {
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      justify-content: flex-start;
      grid-gap: $grid-base * 0.5 $grid-base * 3;
      flex-wrap: wrap;
      max-height: 380px;
    }
  }
}
</style>
